import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  RightOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import "./exam.css";
const { Title, Text } = Typography;
const EXAM_API_BASE =
  process.env.REACT_APP_EXAM_API_BASE_URL ||
  "http://localhost:5000/api/exams";
const DEFAULT_PAGE_SIZE = 10;
const parseJsonSafely = async (response) => {
  const raw = await response.text();
  if (!raw) {
    return {
      status: response.ok,
      message: response.ok ? "Request completed" : "Request failed",
    };
  }
  try {
    return JSON.parse(raw);
  } catch {
    return {
      status: false,
      message: `Server returned an invalid response (${response.status})`,
    };
  }
};
const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};
const getDepartmentName = (record) =>
  record?.department_name ||
  record?.department ||
  record?.dept_name ||
  "";
export default function ManageExamsPage() {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [level, setLevel] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const departmentLoading = loading;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [editDepartmentId, setEditDepartmentId] = useState(null);
  const [editDurationMinutes, setEditDurationMinutes] = useState(30);
  const [editStatus, setEditStatus] = useState("inactive");
  const mapDepartmentOptions = (items = []) =>
    items.map((item) => {
      const deptId = item.department_id;
      const deptName =
        item.department_name ||
        item.department ||
        item.dept_name ||
        "Department name not found";

      return {
        value: String(deptId),
        selectedLabel: deptName,
        searchText: `${deptName} ${deptId}`,
        department_name: deptName,
        department_id: deptId,
        label: deptName,
      };
    });

  const resolveDepartmentName = (record) => {
    const directName =
      getDepartmentName(record);

    if (directName) {
      return directName;
    }

    const option =
      departmentOptions.find(
        (item) =>
          String(item.value) ===
          String(record?.department_id)
      );

    return (
      option?.department_name ||
      "Department name not found"
    );
  };
  const resolveDepartmentDisplay = (record) =>
    resolveDepartmentName(record);
  const loadExams = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (search) {
        params.set("search", search);
      }
      if (status) {
        params.set("status", status);
      }
      if (level) {
        params.set("level", level);
      }
      if (departmentId) {
        params.set("department_id", String(departmentId));
      }
      const response = await fetch(
        `${EXAM_API_BASE}/manage?${params.toString()}`
      );
      const result = await parseJsonSafely(response);
      if (!response.ok || !result?.status) {
        message.error(result?.message || "Unable to load exams");
        setRows([]);
        setTotal(0);
        return;
      }
      const list = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.data?.items)
        ? result.data.items
        : [];
      const pagination =
        result?.pagination ||
        result?.data?.pagination ||
        {};
      setRows(list);

      const filters =
        result?.filters ||
        result?.data?.filters ||
        {};

      setDepartmentOptions(
        mapDepartmentOptions(
          Array.isArray(filters.departments)
            ? filters.departments
            : []
        )
      );

      setTotal(
        Number(
          pagination.total ??
            result?.total ??
            list.length
        ) || 0
      );
    } catch (error) {
      console.error("Load exams error:", error);
      message.error("Unable to connect to the exam server");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, status, level, departmentId]);
  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };
  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
    setLevel("");
    setDepartmentId("");
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  };
  const openEditModal = (exam) => {
    setSelectedExam(exam);
    setEditDepartmentId(
      Number(
        exam.department_id ||
        0
      ) || null
    );
    setEditDurationMinutes(
      Number(
        exam.duration_minutes ||
        30
      )
    );
    setEditStatus(
      String(
        exam.status ||
        "inactive"
      ).toLowerCase()
    );
    setEditModalOpen(true);
  };
  const saveExam = async () => {
    if (!selectedExam) return;
    const departmentId =
      Number(
        editDepartmentId
      );
    const durationMinutes =
      Number(
        editDurationMinutes
      );
    if (
      !Number.isInteger(
        departmentId
      ) ||
      departmentId < 1
    ) {
      message.error(
        "Please enter a valid department ID"
      );
      return;
    }
    if (
      !Number.isInteger(
        durationMinutes
      ) ||
      durationMinutes < 1 ||
      durationMinutes > 1440
    ) {
      message.error(
        "Duration must be between 1 and 1440 minutes"
      );
      return;
    }
    if (
      ![
        "active",
        "inactive",
      ].includes(
        editStatus
      )
    ) {
      message.error(
        "Please select a valid status"
      );
      return;
    }
    try {
      setActionLoading(
        `edit-${selectedExam.exam_id}`
      );
      const response =
        await fetch(
          `${EXAM_API_BASE}/${selectedExam.exam_id}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type":
                "application/json",
            },
            body:
              JSON.stringify({
                department_id:
                  departmentId,
                duration_minutes:
                  durationMinutes,
                status:
                  editStatus,
              }),
          }
        );
      const result =
        await parseJsonSafely(
          response
        );
      if (
        !response.ok ||
        !result?.status
      ) {
        message.error(
          result?.message ||
          "Unable to update exam"
        );
        return;
      }
      message.success(
        result?.message ||
        "Exam updated successfully"
      );
      setEditModalOpen(
        false
      );
      setSelectedExam(
        null
      );
      await loadExams();
    } catch (error) {
      console.error(
        "Update exam error:",
        error
      );
      message.error(
        "Unable to update exam"
      );
    } finally {
      setActionLoading(
        null
      );
    }
  };
  const deleteAttempts = (exam) => {
    Modal.confirm({
      rootClassName: "exam-manage-confirm-modal-root",
      title: "Delete all attempts?",
      width: 560,
      content: (
        <div>
          <p>
            This will permanently delete all attempt history and submitted
            answers for:
          </p>
          <p>
            <strong>{exam.exam_name}</strong>
          </p>
          <p>
            The exam and its questions will remain available.
          </p>
        </div>
      ),
      okText: "Delete Attempts",
      okButtonProps: {
        danger: true,
      },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setActionLoading(`attempts-${exam.exam_id}`);
          const response = await fetch(
            `${EXAM_API_BASE}/${exam.exam_id}/attempts`,
            {
              method: "DELETE",
            }
          );
          const result = await parseJsonSafely(response);
          if (!response.ok || !result?.status) {
            message.error(
              result?.message || "Unable to delete exam attempts"
            );
            return;
          }
          message.success(
            result?.message ||
              "Exam attempts deleted successfully"
          );
          await loadExams();
        } catch (error) {
          console.error("Delete attempts error:", error);
          message.error("Unable to delete exam attempts");
        } finally {
          setActionLoading(null);
        }
      },
    });
  };
  const deleteExam = (exam) => {
    Modal.confirm({
      rootClassName: "exam-manage-confirm-modal-root",
      title: "Delete exam permanently?",
      width: 580,
      content: (
        <div>
          <p>
            You are about to permanently delete:
          </p>
          <p>
            <strong>{exam.exam_name}</strong>
          </p>
          <p>
            Department: <strong>{resolveDepartmentDisplay(exam)}</strong>
          </p>
          <p>
            This removes the exam, questions, attempts, and submitted answers.
            This action cannot be undone.
          </p>
        </div>
      ),
      okText: "Delete Exam",
      okButtonProps: {
        danger: true,
      },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setActionLoading(`delete-${exam.exam_id}`);
          const response = await fetch(
            `${EXAM_API_BASE}/${exam.exam_id}`,
            {
              method: "DELETE",
            }
          );
          const result = await parseJsonSafely(response);
          if (!response.ok || !result?.status) {
            message.error(
              result?.message || "Unable to delete exam"
            );
            return;
          }
          message.success(
            result?.message ||
              "Exam deleted successfully"
          );
          if (rows.length === 1 && page > 1) {
            setPage((current) => current - 1);
          } else {
            await loadExams();
          }
        } catch (error) {
          console.error("Delete exam error:", error);
          message.error("Unable to delete exam");
        } finally {
          setActionLoading(null);
        }
      },
    });
  };
  const isExamActive = (value) =>
    String(
      value || ""
    ).toLowerCase() ===
    "active";
  const renderMobileExamCard = (
    exam,
    index
  ) => {
    const active =
      isExamActive(
        exam.status
      );
    return (
      <div
        className="manage-exam-mobile-card"
        key={exam.exam_id}
      >
        <div className="manage-exam-mobile-card-head">
          <div className="manage-exam-mobile-title">
            <strong>
              {exam.exam_name || "-"}
            </strong>
            <span>
              Exam ID: {exam.exam_id}
            </span>
          </div>
          <Tag
            color={
              active
                ? "green"
                : "default"
            }
          >
            {active
              ? "Active"
              : "Inactive"}
          </Tag>
        </div>
        <div className="manage-exam-mobile-meta">
          <div>
            <span>Sl. No.</span>
            <strong>
              {(page - 1) *
                pageSize +
                index +
                1}
            </strong>
          </div>
          <div>
            <span>Department</span>
            <strong>
              {resolveDepartmentName(exam)}
            </strong>
          </div>
          <div>
            <span>Questions</span>
            <strong>
              {Number(
                exam.question_count ||
                  0
              )}
            </strong>
          </div>
          <div>
            <span>Duration</span>
            <strong>
              {Number(
                exam.duration_minutes ||
                  0
              )}{" "}
              min
            </strong>
          </div>
          <div>
            <span>Attempts</span>
            <strong>
              {Number(
                exam.attempt_count ||
                  0
              )}
            </strong>
          </div>
          <div>
            <span>Submitted</span>
            <strong>
              {Number(
                exam.submitted_attempt_count ||
                  0
              )}
            </strong>
          </div>
          <div className="manage-exam-mobile-created">
            <span>Created</span>
            <strong>
              {formatDateTime(
                exam.created_at
              )}
            </strong>
          </div>
        </div>
        <div className="manage-exam-mobile-actions">
          <Button
            type="text"
            size="small"
            className="manage-exam-action manage-exam-action-edit"
            icon={
              <EditOutlined />
            }
            loading={
              actionLoading ===
              `edit-${exam.exam_id}`
            }
            onClick={() =>
              openEditModal(
                exam
              )
            }
          >
            Edit
          </Button>
          <Button
            type="text"
            size="small"
            className="manage-exam-action manage-exam-action-clear"
            icon={
              <StopOutlined />
            }
            loading={
              actionLoading ===
              `attempts-${exam.exam_id}`
            }
            onClick={() =>
              deleteAttempts(
                exam
              )
            }
          >
            Clear
          </Button>
          <Button
            type="text"
            danger
            size="small"
            className="manage-exam-action manage-exam-action-delete"
            icon={
              <DeleteOutlined />
            }
            loading={
              actionLoading ===
              `delete-${exam.exam_id}`
            }
            onClick={() =>
              deleteExam(
                exam
              )
            }
          >
            Delete
          </Button>
        </div>
      </div>
    );
  };
  const columns = useMemo(
    () => [
      {
        title: "Sl. No.",
        width: 78,
        render: (_, __, index) =>
          (page - 1) * pageSize + index + 1,
      },
      {
        title: "Exam",
        dataIndex: "exam_name",
        width: 260,
        render: (value, record) => (
          <div className="manage-exam-name">
            <strong>{value || "-"}</strong>
            <span>Exam ID: {record.exam_id}</span>
          </div>
        ),
      },
      {
        title: "Level",
        dataIndex: "level",
        width: 110,
        render: (value) => (
          <Tag color="blue">
            {value ? String(value).replaceAll("_", " ") : "-"}
          </Tag>
        ),
      },
      {
        title: "Department",
        width: 190,
        render: (_, record) => (
          <div className="manage-exam-name">
            <strong>
              {resolveDepartmentName(record)}
            </strong>
          </div>
        ),
      },
      {
        title: "Questions",
        dataIndex: "question_count",
        width: 105,
        align: "center",
        render: (value) => Number(value || 0),
      },
      {
        title: "Duration",
        dataIndex: "duration_minutes",
        width: 110,
        render: (value) => `${Number(value || 0)} min`,
      },
      {
        title: "Attempts",
        dataIndex: "attempt_count",
        width: 100,
        align: "center",
        render: (value) => Number(value || 0),
      },
      {
        title: "Submitted",
        dataIndex: "submitted_attempt_count",
        width: 105,
        align: "center",
        render: (value) => Number(value || 0),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: 105,
        render: (value) => {
          const active =
            String(
              value || ""
            ).toLowerCase() ===
            "active";
          return (
            <Tag
              color={
                active
                  ? "green"
                  : "default"
              }
            >
              {active
                ? "Active"
                : "Inactive"}
            </Tag>
          );
        },
      },
      {
        title: "Created",
        dataIndex: "created_at",
        width: 190,
        responsive: ["xl"],
        render: (value) => (
          <span className="manage-exam-created-value">
            {formatDateTime(value)}
          </span>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 220,
        align: "center",
        render: (_, record) => (
          <div className="manage-exam-row-actions">
            <Button
              type="text"
              size="small"
              title="Edit Exam"
              aria-label="Edit Exam"
              icon={<EditOutlined />}
              className="manage-exam-action manage-exam-action-edit"
              loading={
                actionLoading ===
                `edit-${record.exam_id}`
              }
              onClick={() =>
                openEditModal(record)
              }
            >
              Edit
            </Button>
            <Button
              type="text"
              size="small"
              title="Clear Attempts"
              aria-label="Clear Attempts"
              icon={<StopOutlined />}
              className="manage-exam-action manage-exam-action-clear"
              loading={
                actionLoading ===
                `attempts-${record.exam_id}`
              }
              onClick={() =>
                deleteAttempts(record)
              }
            >
              Clear
            </Button>
            <Button
              type="text"
              danger
              size="small"
              title="Delete Exam"
              aria-label="Delete Exam"
              icon={<DeleteOutlined />}
              className="manage-exam-action manage-exam-action-delete"
              loading={
                actionLoading ===
                `delete-${record.exam_id}`
              }
              onClick={() =>
                deleteExam(record)
              }
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, actionLoading, rows.length, departmentOptions]
  );
  return (
    <div className="exam-management-page exam-management-manage-page">
      <section className="exam-management-hero">
        <div className="exam-management-hero-pattern" />
        <div className="exam-management-hero-content">
          <div className="exam-management-hero-copy">
            <div className="exam-management-breadcrumb">
              <span className="exam-management-breadcrumb-home">
                <HomeOutlined />
                <span>SM-ART</span>
              </span>
              <RightOutlined />
              <span>Exam Management</span>
              <RightOutlined />
              <strong>Manage Exams</strong>
            </div>
            <Title level={3}>Manage Exams</Title>
            <Text>
              Manage assessments, duration, status and attempts.
            </Text>
          </div>
        </div>
      </section>
      <div className="exam-management-content-panel">
        <div className="manage-exam-toolbar">
          <div className="manage-exam-toolbar-field manage-exam-toolbar-search">
            <Input
              allowClear
              value={searchInput}
              prefix={<SearchOutlined />}
              placeholder="Search exam name or exam ID"
              onChange={(event) =>
                setSearchInput(event.target.value)
              }
              onPressEnter={handleSearch}
            />
          </div>
          {/* <div className="manage-exam-toolbar-field">
            <Select
              allowClear
              showSearch
              loading={departmentLoading}
              value={departmentId || undefined}
              placeholder="All Departments"
              options={departmentOptions}
              optionFilterProp="searchText"
              optionLabelProp="selectedLabel"
              popupClassName="manage-training-department-dropdown"
              filterOption={(input, option) =>
                String(option?.searchText || "")
                  .toLowerCase()
                  .includes(
                    String(input || "").toLowerCase()
                  )
              }
              onChange={(value) => {
                setDepartmentId(value || "");
                setPage(1);
              }}
            />
          </div> */}
          <div className="manage-exam-toolbar-field">
            <Select
              allowClear
              value={level || undefined}
              placeholder="All Levels"
              options={[
                { value: "level_1", label: "Level 1" },
                { value: "level_2", label: "Level 2" },
                { value: "level_3", label: "Level 3" },
              ]}
              onChange={(value) => {
                setLevel(value || "");
                setPage(1);
              }}
            />
          </div>
          <div className="manage-exam-toolbar-field">
            <Select
              allowClear
              value={status || undefined}
              placeholder="All Status"
              options={[
                {
                  value: "active",
                  label: "Active",
                },
                {
                  value: "inactive",
                  label: "Inactive",
                },
              ]}
              onChange={(value) => {
                setStatus(value || "");
                setPage(1);
              }}
            />
          </div>
          <div className="manage-exam-toolbar-actions">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              className="manage-exam-toolbar-button manage-exam-search-button"
            >
              Search
            </Button>
            <Button
              onClick={handleReset}
              className="manage-exam-toolbar-button manage-exam-secondary-button"
            >
              Reset
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadExams}
              loading={loading}
              className="manage-exam-toolbar-button manage-exam-secondary-button"
            >
              Refresh
            </Button>
          </div>
        </div>
      <div className="manage-exam-table-wrap">
        {loading ? (
          <div className="manage-exam-loading">
            <Spin size="large" />
            <span>Loading exams...</span>
          </div>
        ) : (
          <>
            <div className="manage-exam-desktop-table">
              <Table
                rowKey="exam_id"
                columns={columns}
                dataSource={rows}
                pagination={false}
                size="middle"
                scroll={{
                  x: 1000,
                }}
                locale={{
                  emptyText: "No exams found",
                }}
              />
            </div>
            <div className="manage-exam-mobile-list">
              {rows.length ? (
                rows.map(
                  renderMobileExamCard
                )
              ) : (
                <div className="manage-exam-mobile-empty">
                  No exams found
                </div>
              )}
            </div>
            <div className="manage-exam-pagination">
              <span>
                Showing{" "}
                {total
                  ? (page - 1) * pageSize + 1
                  : 0}
                –
                {Math.min(page * pageSize, total)}{" "}
                of {total}
              </span>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                showLessItems
                responsive
                pageSizeOptions={[10, 20, 50, 100]}
                onChange={(nextPage, nextPageSize) => {
                  if (nextPageSize !== pageSize) {
                    setPageSize(nextPageSize);
                    setPage(1);
                    return;
                  }
                  setPage(nextPage);
                }}
              />
            </div>
          </>
        )}
      </div>
      </div>
      <Modal
        className="manage-exam-edit-modal"
        rootClassName="manage-exam-modal-root"
        title="Edit Exam"
        open={editModalOpen}
        okText="Save Changes"
        cancelText="Cancel"
        okButtonProps={{
          className: "manage-exam-modal-save-button",
        }}
        cancelButtonProps={{
          className: "manage-exam-modal-cancel-button",
        }}
        confirmLoading={
          selectedExam &&
          actionLoading ===
            `edit-${selectedExam.exam_id}`
        }
        onCancel={() => {
          if (actionLoading) return;
          setEditModalOpen(false);
          setSelectedExam(null);
        }}
        onOk={saveExam}
      >
        <div className="manage-exam-duration-modal">
          <div className="manage-exam-modal-exam-row">
            <span>Exam</span>
            <strong>{selectedExam?.exam_name || "-"}</strong>
          </div>
          <div className="manage-exam-modal-exam-row">
            <span>Current Department</span>
            <strong>
              {selectedExam
                ? resolveDepartmentDisplay(selectedExam)
                : "-"}
            </strong>
          </div>
          <div className="manage-exam-modal-field">
            <label>Department</label>
            <Select
              showSearch
              loading={departmentLoading}
              value={
                editDepartmentId
                  ? String(editDepartmentId)
                  : undefined
              }
              placeholder="Select Department"
              options={departmentOptions}
              optionFilterProp="searchText"
              optionLabelProp="selectedLabel"
              popupClassName="manage-training-department-dropdown"
              filterOption={(input, option) =>
                String(option?.searchText || "")
                  .toLowerCase()
                  .includes(
                    String(input || "").toLowerCase()
                  )
              }
              onChange={(value) =>
                setEditDepartmentId(
                  value
                    ? Number(value)
                    : null
                )
              }
            />
          </div>
          <div className="manage-exam-modal-field">
            <label>Duration (minutes)</label>
            <InputNumber
              min={1}
              max={1440}
              precision={0}
              value={editDurationMinutes}
              onChange={setEditDurationMinutes}
            />
          </div>
          <div className="manage-exam-modal-field">
            <label>Status</label>
            <Select
              value={editStatus}
              onChange={setEditStatus}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
            />
          </div>
          <p>
            Changes apply to this exam. Existing attempt history is preserved.
          </p>
        </div>
      </Modal>
    </div>
  );
}
